import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const faqs = [
  {
    question: "Does it cost me any money to get an offer?",
    answer: "No, never. Our cash offers are 100% free and come with zero obligation to sell. You have nothing to lose by seeing what we can offer."
  },
  {
    question: "Are there any hidden fees or agent commissions?",
    answer: "None at all. Since we buy exactly from you, there are no real estate agents involved, which means no 6-10% commissions. We also cover all standard closing costs and title fees."
  },
  {
    question: "My land is messy and overgrown. Do I need to clean it up?",
    answer: "Absolutely not. We buy land exactly as it is right now. You don't need to clear brush, remove trash, or do any work to the property before selling."
  },
  {
    question: "What if I owe back taxes on the property?",
    answer: "That's very common! We handle all the messy paperwork for you. The back taxes are simply paid off from the final purchase price at closing. You don't have to pay anything out of pocket."
  },
  {
    question: "How quickly can I get my money?",
    answer: "We typically close in 14 to 30 days, depending on how fast the local title company can process the paperwork. Once everything is signed, the title company wires the funds directly to your bank account or hands you a check."
  }
];

function FAQItem({ faq, isOpen, onToggle }) {
  return (
    <div className="border-b border-stone-200 last:border-0">
      <button
        className="w-full py-6 flex justify-between items-center text-left focus:outline-none group"
        onClick={onToggle}
      >
        <span className={`text-lg font-semibold transition-colors ${isOpen ? 'text-brand-600' : 'text-stone-900 group-hover:text-brand-600'}`}>
          {faq.question}
        </span>
        <div className={`ml-4 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-brand-100 text-brand-600' : 'bg-stone-100 text-stone-500 group-hover:bg-brand-50'}`}>
          <SafeIcon icon={isOpen ? FiChevronUp : FiChevronDown} className="text-xl" />
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="pb-6 text-stone-600 leading-relaxed pr-8">
              {faq.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section id="faq" className="py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-brand-600 font-semibold tracking-wide uppercase text-sm mb-2">Got Questions?</h2>
          <h3 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4">Frequently Asked Questions</h3>
          <p className="text-stone-600 text-lg">We keep things simple. Here are honest answers to the most common questions we get from landowners.</p>
        </div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-stone-50 rounded-3xl p-6 md:p-10 shadow-sm border border-stone-100"
        >
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              faq={faq}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
}